import { Typography, Card, CardContent, CardHeader, Button, Tooltip, Box } from "@mui/material";
import { useEffect, useState } from "react";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { UpsertAction } from "../Enums";
import { DevelopmentPlanItem, ProficiencyGraph } from "../models/dtos";
import DevelopmentItemUpsertComponent, { CreateDevelopmentItemEx } from "./DevelopmentItemUpsertComponent";
import { CreateDevelopmentItem } from "../apiClients/DevelopmentClient";
import useViewportMode from "../hooks/useViewportMode";

export interface FinalStepDevPlanItem extends DevelopmentPlanItem {
    proficiency: ProficiencyGraph;
}
export interface DevelopmentPlanDisplayComponentProps {
    talentId: string;
    developmentItems: FinalStepDevPlanItem[];
    onStartDevelopmentPlan: () => void;
    onReadyToSubmit: (itemsToCreate: CreateDevelopmentItem[]) => void;
}

const DevelopmentPlanDisplayComponent = ({ talentId, developmentItems, onStartDevelopmentPlan = () => { }, onReadyToSubmit = () => { } }: DevelopmentPlanDisplayComponentProps) => {

    const [developmentPlanItemsLeftToSave, setDevelopmentPlanItemsLeftToSave] = useState<FinalStepDevPlanItem[]>(
        () => developmentItems.filter(item => item.Type === 'Proficiency')
    );
    const [developmentPlanDisabled, setDevelopmentPlanDisabled] = useState<boolean>(true);
    const viewport = useViewportMode();

    const [itemsToReturnToParent, setItemsToReturnToParent] = useState<CreateDevelopmentItemEx[]>([]);

    useEffect(() => {
        
        const editableItems = developmentItems.filter(item => item.Type === 'Proficiency');
        setDevelopmentPlanItemsLeftToSave(editableItems);
    }, [developmentItems]);

    const handleStartDevelopmentPlan = () => {
        setDevelopmentPlanDisabled(false);
        onStartDevelopmentPlan();
        if (developmentPlanItemsLeftToSave.length === 0)
            onReadyToSubmit([]);
    }

    const handleDevelopmentPlanItemChange = async (item: CreateDevelopmentItemEx) => {
        // Add the item to the list to return to the parent component
        const localItemsToReturn = [...itemsToReturnToParent, item];
        setItemsToReturnToParent(localItemsToReturn);

        const itemsLeft = developmentPlanItemsLeftToSave.filter(f => f.Id !== item.Id);
        setDevelopmentPlanItemsLeftToSave(itemsLeft);

        if (itemsLeft.length === 0)
            onReadyToSubmit(localItemsToReturn);
    }

    const determineDevItemMode = (id: string): UpsertAction => {
        if (!developmentPlanDisabled && developmentPlanItemsLeftToSave.find(f => f.Id === id))
            return UpsertAction.Create
        else
            return UpsertAction.View
    }

    return (
        <>
            <Tooltip describeChild title="Starting the development plan will disable the ability to go back and make any changes to this assessment.">
                <Button
                    sx={{ width: viewport.isDesktop ? '25%' : '100%', minHeight: 44, alignSelf: 'center' }}
                    disabled={!developmentPlanDisabled}
                    variant="outlined"
                    onClick={handleStartDevelopmentPlan}
                >Start Development Plan</Button>
            </Tooltip>

            <Box sx={{
                display: viewport.isDesktop ? 'flex' : 'grid',
                gridTemplateColumns: viewport.isTablet ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                flexWrap: viewport.isDesktop ? 'wrap' : undefined,
                gap: viewport.isDesktop ? 0 : 2,
                alignContent: 'space-between',
                justifyContent: 'space-evenly',
                mt: viewport.isDesktop ? 0 : 2,
            }}>
                {developmentItems.map((di) => (
                    <Box component="section" key={di.Id} aria-label={`Development plan item: ${di.Name}`} sx={{ minWidth: 0 }}>
                        <ConditionalDisplay ifTrue={di.Type === 'PII'}>
                            <Card variant="outlined" sx={{ height: viewport.isDesktop ? 540 : 'auto' }}>
                                <CardHeader sx={{ fontSize: 12, textAlign: "left", backgroundColor: "primary" }} title={`PII: ${di.Name}`} />
                                <CardContent>
                                    <Typography>{di.Narrative}</Typography>
                                </CardContent>
                            </Card>
                        </ConditionalDisplay>
                        <ConditionalDisplay ifTrue={di.Type === 'Proficiency'}>
                            <Card variant="outlined" sx={{ height: viewport.isDesktop ? 540 : 'auto' }}>
                                <CardHeader sx={{ fontSize: 11, textAlign: "left", backgroundColor: "secondary" }} title={`${di.Narrative}`} />
                                <CardContent>
                                    <DevelopmentItemUpsertComponent onChange={(id) => handleDevelopmentPlanItemChange(id)} mode={determineDevItemMode(di.Id)} talentId={talentId} developmentItemId={di.Id} proficiency={di.proficiency} />
                                </CardContent>
                            </Card>
                        </ConditionalDisplay>
                    </Box>
                ))}
            </Box>
        </>
    );
}

export default DevelopmentPlanDisplayComponent;
